/**
 * React context that holds Cognito tokens in memory and exposes
 * login/logout/claims to the rest of the SPA.
 *
 * Per design.md, tokens are NEVER persisted to localStorage or cookies.
 * That means a full-page reload loses the session and the user goes
 * through the Hosted UI again — acceptable for a non-production demo
 * and a meaningful security property.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  beginLogin,
  beginLogout,
  decodeJwtPayload,
  type CognitoIdTokenClaims,
  type CognitoTokenResponse,
} from "./oauth";

export interface AuthSession {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  claims: CognitoIdTokenClaims;
  /** Roles derived from `cognito:groups`. */
  groups: string[];
  /** Convenience: true when the user is in the `Admin` group. */
  isAdmin: boolean;
  /** Convenience: true when the user is in the `Consumer` group. */
  isConsumer: boolean;
  /** Millisecond timestamp when the ID token expires. */
  expiresAtMs: number;
}

export interface AuthContextValue {
  session: AuthSession | null;
  /** Begin the OAuth2 redirect to the Cognito Hosted UI. */
  login: (postLoginPath?: string) => void;
  /** Redirect to the Cognito logout endpoint. */
  logout: () => void;
  /**
   * Accept tokens obtained from a code exchange (called by the callback page)
   * and populate the session.
   */
  acceptTokens: (tokens: CognitoTokenResponse) => void;
  /** Clear the in-memory session (used by logout or on expiry). */
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Build an AuthSession from a Cognito token response, or null on bad token. */
export function sessionFromTokens(
  tokens: CognitoTokenResponse,
): AuthSession | null {
  const claims = decodeJwtPayload(tokens.id_token);
  if (!claims) return null;
  const groups = Array.isArray(claims["cognito:groups"])
    ? claims["cognito:groups"]
    : [];
  const session: AuthSession = {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    claims,
    groups,
    isAdmin: groups.includes("Admin"),
    isConsumer: groups.includes("Consumer"),
    expiresAtMs: claims.exp * 1000,
  };
  if (tokens.refresh_token) {
    session.refreshToken = tokens.refresh_token;
  }
  return session;
}

interface AuthProviderProps {
  children: ReactNode;
  /**
   * Optional initial session. Tests inject a pre-populated session so
   * they can exercise role-gated UI without going through OAuth2.
   */
  initialSession?: AuthSession | null;
}

export function AuthProvider({ children, initialSession }: AuthProviderProps) {
  const [session, setSession] = useState<AuthSession | null>(
    initialSession ?? null,
  );
  const expiryTimerRef = useRef<number | undefined>(undefined);

  /** Clear any pending expiry timer. */
  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current !== undefined) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = undefined;
    }
  }, []);

  /** Schedule automatic logout at token expiry. */
  const scheduleExpiry = useCallback(
    (expiresAtMs: number) => {
      clearExpiryTimer();
      const msUntilExpiry = expiresAtMs - Date.now();
      if (msUntilExpiry <= 0) {
        setSession(null);
        return;
      }
      expiryTimerRef.current = window.setTimeout(() => {
        setSession(null);
      }, msUntilExpiry);
    },
    [clearExpiryTimer],
  );

  useEffect(() => {
    if (session) scheduleExpiry(session.expiresAtMs);
    return () => clearExpiryTimer();
  }, [session, scheduleExpiry, clearExpiryTimer]);

  const login = useCallback((postLoginPath?: string) => {
    // Be defensive: if the deployed bundle is missing
    // `VITE_HOSTED_UI_DOMAIN` / `VITE_USER_POOL_CLIENT_ID`,
    // `beginLogin` throws. UI surfaces (`RoleNav`, `Home`,
    // `RouteGuard`) already render a friendly notice when
    // `isAuthConfigured()` is false, but a stray caller would still
    // present a click that silently no-ops. Catch and log so the
    // failure is visible in the console without taking the page
    // down with an unhandled promise rejection.
    try {
      beginLogin(postLoginPath ?? window.location.pathname);
    } catch (err) {
      console.error(
        "Hosted UI redirect could not be started — VITE_HOSTED_UI_DOMAIN / VITE_USER_POOL_CLIENT_ID are unset in this build.",
        err,
      );
    }
  }, []);

  const logout = useCallback(() => {
    clearExpiryTimer();
    setSession(null);
    beginLogout();
  }, [clearExpiryTimer]);

  const acceptTokens = useCallback((tokens: CognitoTokenResponse) => {
    const next = sessionFromTokens(tokens);
    if (next) setSession(next);
  }, []);

  const clearSession = useCallback(() => {
    clearExpiryTimer();
    setSession(null);
  }, [clearExpiryTimer]);

  const value = useMemo<AuthContextValue>(
    () => ({ session, login, logout, acceptTokens, clearSession }),
    [session, login, logout, acceptTokens, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook for consumers of AuthContext. Throws if used outside AuthProvider. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
